/*
 * Copyright (c) 2021 CalAmp Corp.  All Rights Reserved
 */
import {batch} from 'react-redux';
// eslint-disable-next-line no-restricted-imports
import {NextRouter} from 'next/router';
import {
  deleteLocalStorage,
  localPersistence
} from '@ion/core/src/capabilities/storage';
import {error} from '@ion/core/src/logger/logger';
import {entityId} from '@ion/core/src/services/ConnectUtils';
import {
  authenticate,
  currentUser,
  getAccount,
  logout
} from '@ion/core/src/services/SDConnect';
import {setToken} from '@ion/core/src/services/SDConnectUtils';
import {setUserTimeZone} from '@ion/core/src/timezone/timezone';
import {User} from '@ion/core/src/types/entities';
import {ServiceError} from '@ion/core/src/types/services';
import {setUserUnit} from '@ion/core/src/units/helpers';
import {Unit} from '@ion/core/src/units/units';
import {decode, encode} from '@ion/core/src/utils/Cipher';
import AssetObserver from '@ion/services/src/websocket/AssetObserver';
import changePage, {
  getRoute,
  Route
} from '@/routes/RouteDefinitions';
import {
  deleteSession,
  getSession,
  setSession
} from '@/security/Session';
import {ApplicationKeys} from '@/types/application';
import {
  APPLICATION_INITIALIZATION_ERROR,
  initializationComplete,
  initialize,
  metricsBegin,
  preinitialization
} from './initialization';
import {toggleUserPrompt} from './user-prompt';

export const LOGIN_REQUEST       = 'LOGIN_REQUEST';
export const LOGIN_SUCCEEDED     = 'LOGIN_SUCCEEDED';
export const LOGIN_FAILED        = 'LOGIN_FAILED';
export const PERSIST_CREDENTIALS = 'PERSIST_CREDENTIALS';
export const LOGOUT_REQUEST      = 'LOGOUT_REQUEST';
export const LOGOUT_FAILED       = 'LOGOUT_FAILED';
export const UPDATE_CURRENT_USER = 'UPDATE_CURRENT_USER';
export const EULA_DIALOG_SHOW    = 'EULA_DIALOG_SHOW';
export const SET_CURRENT_ACCOUNT = 'SET_CURRENT_ACCOUNT';

/**
 * Provides a pre-authentication check which first attempts to retrieve the current
 * user if the user has an session timestamp persisted that is within the same day
 * (CTC timeouts are based on a one hour of no activity interval; however, we retry
 * for the duration of a day as the session may continue to be renewed). If returned
 * successfully confirms that the users current session is still active, otherwise,
 * if the call fails, returns null and the user is redirected to the login page.
 */
const preauth = async() => {
  const session = getSession();
  if (session) {
    try {
      return await currentUser(session);
    } catch (err) {
      error('Unable to retrieve authenticated User Entity', err);
    }
  }
};

/**
 * Persists the authenticated user's credentials and remember me settings to local
 * storage.
 */
export const persistUser = (persist: boolean, username: string, password: string) => {
  if (persist) {
    localPersistence(ApplicationKeys.BMWPortal, 'login', {
      credentials : {username, password: encode(password)},
      rememberMe  : true
    });
  } else {
    deleteLocalStorage(ApplicationKeys.BMWPortal);
  }
};

/**
 * The login action creator provides the single point of entry for authenticating with
 * CTC Services.
 *
 * If invoked without a username and password, the application will simply execute the
 * preauth phase which will verify if the user has an active sessions, otherwise, if
 * invoked with a username and password, the preauth phase will still be verified and
 * if the session is invalid the user will be authenticated with the given credentials.
 */
export const login = (username?: string, password?: string, router?: NextRouter) => {
  return async(dispatch, getState) => {
    metricsBegin();
    (username && password) && dispatch({type: LOGIN_REQUEST});
    let user = await preauth();
    try {
      if (!user && username && password) {
        const persist = getState().login.persistCredentials;
        user = await authenticate(username, password);
        persistUser(persist, username, password);
      } else if (!user) {
        changePage(Route.LOGIN);
        return false;
      }
      const {account} = user;
      const id        = entityId(account?.href);
      const currentAccount = await getAccount(id);
      dispatch({type: SET_CURRENT_ACCOUNT, currentAccount});
      setSession();
      setUserUnit(user.systemOfUnits.value as Unit);
      setUserTimeZone(user.timeZone);
      dispatch({type: LOGIN_SUCCEEDED, user});
      if (!getState().initialization.preInitializationComplete) {
        dispatch(preinitialization());
      }
      dispatch(initialize(user, currentAccount, router));
    } catch (error) {
      batch(() => {
        const serviceError = error as ServiceError;
        if (!serviceError?.isUnauthorizedError) {
          dispatch(toggleUserPrompt({
            message     : 'notifications:error.client',
            actionLabel : 'notifications:warning.actionLabel',
            autoHide    : false
          }));
        }
        dispatch({type: LOGIN_FAILED, isInvalid: serviceError?.isUnauthorizedError});
      });
    }
  };
};

/**
 * @signout
 *
 * The signout function logs the user out of CTC, effectively ending their
 * session, followed by reloading and redirecting them back to the login page.
 */
export const signout = (isEulaDeclined = false) => {
  deleteLocalStorage(ApplicationKeys.BMWPortalPage);
  deleteSession();
  AssetObserver.removeAllObservers();
  return async dispatch => {
    try {
      dispatch({type: LOGOUT_REQUEST});
      isEulaDeclined && dispatch({type: APPLICATION_INITIALIZATION_ERROR});
      await logout();
    } catch (error) {
      dispatch({type: LOGOUT_FAILED});
    } finally {
      !isEulaDeclined && location.assign(getRoute(Route.LOGIN));
      setToken('');
    }
  };
};

/**
 * Invoked when the user's CTC session has expired.
 */
export const sessionTimeout = () => {
  return () => {
    deleteSession();
    location.assign(getRoute(Route.LOGIN));
  };
};

/**
 * @persist
 *
 * Invoked when the user chooses to persist their login credentials.
 */
export const persistCredentials = (persist: boolean) => {
  return {type: PERSIST_CREDENTIALS, persist};
};

/**
 * Invoked when the user needs to be updated (ex. after accepting the EULA).
 */
export const updateUser = (user: User, isNewUser: boolean = false) => {
  return (dispatch: Function) => {
    if (isNewUser) {
      dispatch(initializationComplete({user}));
    }
    dispatch({type: UPDATE_CURRENT_USER, user});
  };
};

/**
 * Invoked when the EULA dialog is to be shown or hidden.
 */
export const showEULADialog = (show: boolean) => {
  return {type: EULA_DIALOG_SHOW, show};
};
